import { AlternativeRadioInput, RadioInputProps } from "@components/RadioInput";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { SHARED_PALETTE } from "@every.org/common/src/display/palette";

import { Button } from "src/components/Button";
import { DropZone } from "src/components/DropZone";
import { MAX_INPUT_WIDTH } from "src/components/Nonprofit/EditForm/shared";
import { colorCssVars, lightBgThemeCss } from "src/theme/color";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { textSizeCss, TextSize, FontWeight } from "src/theme/text";

const fullAbsoluteSizeCss = css`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

export const StyledDropZone = styled(DropZone)`
  ${fullAbsoluteSizeCss}
`;

export const LoadingOverlay = styled.div`
  ${fullAbsoluteSizeCss};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px;
`;

const buttonCssBase = css`
  border-radius: 50%;
  position: absolute;
  padding: ${spacing.xs};
  ${lightBgThemeCss};
`;

export const FullWidthButton = styled(Button)`
  ${fullAbsoluteSizeCss};
  width: 100%;
`;

export const PlusButton = styled(Button)`
  ${fullAbsoluteSizeCss};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;

  &:hover {
    background-color: var(${colorCssVars.input.background.hover});
  }
`;

export const PlusIconContainer = styled.div`
  ${lightBgThemeCss};
  padding: ${spacing.xs};
  border-radius: 500px;
`;

export const plusIconContentCss = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  align-items: center;
  justify-content: center;
`;

export const EditButton = styled(Button)`
  ${buttonCssBase};
  top: ${spacing.m};
  right: ${spacing.m};
`;

export const Image = styled.div<{
  url: string | null;
}>`
  ${({ url }) => url && `background-image: url(${url});`}
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  background-color: var(${colorCssVars.input.background.default});
  position: relative;
  border-radius: 8px;

  max-width: ${MAX_INPUT_WIDTH};
  width: 100%;
  padding-top: 50%;
`;

export const ImageContainer = styled.div`
  width: 100%;
  ${verticalStackCss.m};
  text-align: center;
  position: relative;

  ${FullWidthButton} {
    z-index: 1;
  }
  ${EditButton} {
    z-index: 2;
  }
`;

export const DropZoneTitle = styled.span`
  ${textSizeCss[TextSize.m]};
  font-weight: ${FontWeight.BOLD};
`;

export const StyledAlternativeRadioInput = styled(
  AlternativeRadioInput
)<RadioInputProps>`
  ${horizontalStackCss.s};
  align-items: center;
  padding: ${spacing.m};
  background: var(${colorCssVars.background.faded});
  border-radius: 8px;
  border: 2px solid var(${colorCssVars.background.faded});
  cursor: pointer;
  color: var(${colorCssVars.accent.large});
  font-weight: ${FontWeight.MEDIUM};
  ${({ checked }) =>
    checked &&
    `
  background: var(${colorCssVars.background.normal});
  border-color: var(${colorCssVars.accent.large});
`}
  input[type="radio"][data-focus-visible-added]:focus + svg {
      border: 2px solid ${SHARED_PALETTE.focus};
      border-radius: 500px;
    }
  }
`;

export const textInputBoxResetStyles = css`
  font-weight: ${FontWeight.REGULAR};
  &,
  &:not(:focus-within):hover,
  &:focus-within {
    background: none;
    border: none;
    padding: unset;
    box-shadow: none;
  }
`;
